import React, { useEffect, useState } from 'react';
import { Form, Row, Col, Select } from 'antd';
import { Input } from 'components/gyramais';
import { formatAndNotificateError, states, formatString } from 'utils';
import { useMutation } from '@apollo/client';
import POSTAL_CODE_TO_ADDRESS from './graphql/postalCodeToAddress.gql';
import {
  addressPrefix,
  ufs,
  municipalities,
} from '../../BadesulMicrocreditoBusinessUserDrawer/inputOptions.js';

import { selectSearcher } from 'utils/select';
const { Item } = Form;
const { Option } = Select;

const sizes = {
  oneThird: { xs: 24, sm: 24, md: 8, lg: 8, xl: 8 },
  twoThirds: { xs: 24, sm: 24, md: 16, lg: 16, xl: 16 },
  full: { xs: 24, sm: 24, md: 24, lg: 24, xl: 24 },
  half: { xs: 24, sm: 24, md: 12, lg: 12, xl: 12 },
};

/**
 * @typedef {import('antd').ColProps & {
 *   fieldOrder: number
 * }} AddressFomCol
 *
 * @type {React.FC<{
 *  address?: Object
 *  form: any
 *  flow?: string
 *  onlyColumns?: boolean
 *  postalCodePlaceholder?: string
 *  fieldProps: {
 *    postalCode: AddressFomCol
 *    district: AddressFomCol
 *    street: AddressFomCol
 *    number: AddressFomCol
 *    complement: AddressFomCol
 *    state: AddressFomCol
 *    city: AddressFomCol
 *  }
 *  business: Boolean
 * }>}
 */
const AddressForm = ({
  address: {
    id,
    postalCode,
    district,
    prefix,
    street,
    number,
    complement,
    state,
    city,
    cityCode,
    stateCode,
  } = {},
  form,
}) => {
  const [searched, setSearched] = useState(false);
  const [selectedUF, setSelectedUF] = useState(null);

  const handleError = (error) => {
    formatAndNotificateError(error);
    // GYR-1332: CEP inválido/indisponível deve habilitar o preenchimento manual,
    // sem apagar o que já foi digitado (bairro/UF/cidade). Também remove refs
    // inexistentes (`setAutoFilledFields`, `flow`) que lançavam ReferenceError aqui.
    setSearched(true);
  };

  const formatStreet = (_street) => {
    if (!_street) return '';

    let street = _street.trim();
    let prefix = '-';

    const streetPrefix = street.split(' ')[0];
    if (addressPrefix.includes(streetPrefix)) {
      prefix = streetPrefix;
      street = street.replace(streetPrefix, '').trim();
    }

    return [prefix, street];
  };

  const formatUF = (ufCode, ufName) => {
    if (ufCode) {
      // GYR-1332: `ufs` items are { value: IBGE, name: sigla } — there is no `code`.
      // Look up by `value` so the prefill of an existing address keeps the UF (was returning '').
      return [ufCode, ufs.find((uf) => uf.value === ufCode)?.name || ''];
    }
    if (ufName) {
      let ufItem = ufs.find((uf) => uf.name.toLowerCase() === ufName.toLowerCase());
      if (ufItem) {
        return [ufItem.value, ufItem.name];
      }

      // Tem chance do state estar por extenso
      const _uf = Object.keys(states).find(
        (key) => states[key].toLowerCase() === ufName.toLowerCase(),
      );
      if (_uf) {
        ufItem = ufs.find((uf) => uf.name.toLowerCase() === _uf.toLowerCase());
        if (ufItem) {
          return [ufItem.value, ufItem.name];
        }
      }
    }
    return ['', ''];
  };
  const formatCity = (cityCode, cityName) => {
    if (cityCode) {
      const cityItem = municipalities.find((city) => city.value === cityCode);
      if (cityItem) {
        return [cityItem.value, cityItem.name];
      }
    }
    if (cityName) {
      const cityItem = municipalities.find(
        (city) => city.name.toLowerCase() === cityName.toLowerCase(),
      );
      if (cityItem) {
        return [cityItem.value, cityItem.name];
      }
    }
    return ['', ''];
  };

  const [postalCodeToAddress, { loading }] = useMutation(POSTAL_CODE_TO_ADDRESS, {
    onError: (error) => handleError(error),
    onCompleted: ({ postalCodeToAddress }) => {
      if (postalCodeToAddress) {
        const { street, district, city, state } = postalCodeToAddress;

        setSearched(true);

        form.setFieldsValue({
          prefix: formatStreet(street)[0],
          street: formatStreet(street)[1],
          district,
          cityCode: formatCity('', city)[0],
          city: formatCity('', city)[1],
          stateCode: formatUF('', state)[0],
          state: formatUF('', state)[1],
        });
        setSelectedUF(formatUF('', state)[0]);
      }

      return true;
    },
  });

  useEffect(() => {
    if (!id) {
      return;
    }

    setSearched(true);

    const [_prefix, _street] = formatStreet(street || '');
    const [_stateCode, _state] = formatUF(stateCode, states[state]);
    const [_cityCode, _city] = formatCity(cityCode, city);
    const formObj = {
      id,
      postalCode: postalCode ? formatString(postalCode, 'cep') : '',
      district,
      prefix: prefix || _prefix,
      street: _street,
      number,
      complement,
      stateCode: _stateCode,
      state: _state,
      city: _city,
      cityCode: _cityCode,
    };

    form.setFieldsValue(formObj);
    setSelectedUF(_stateCode);
  }, [id]);

  const availableCityes = municipalities.filter(({ uf }) => uf === selectedUF);

  return (
    <Form layout="vertical" form={form}>
      <Item name="state" className="hidden" />
      <Item name="city" className="hidden" />
      <Row gutter={12}>
        <Col {...sizes.full} key="postalCode">
          <Item
            name="postalCode"
            rules={[{ required: true, message: 'Por favor insira o CEP.' }]}
            label={'CEP'}
          >
            <Input
              type="masked"
              className="ant-input"
              guide
              mask="cep"
              placeholder={'-'}
              onKeyUp={(e) => {
                const postalCode = e.target.value.replace(/[_-\s]/g, '');
                if (postalCode.replace(/[_-\s]/g, '').length === 8 && !loading) {
                  postalCodeToAddress({ variables: { postalCode } });
                }
              }}
            />
          </Item>
        </Col>
        <Col {...sizes.oneThird} key="state">
          <Item
            name="stateCode"
            rules={[{ required: true, message: 'Por favor insira o UF do Estado.' }]}
            label={'UF'}
          >
            <Select
              className="gyramais-input"
              loading={loading}
              placeholder="UF"
              showSearch
              disabled={!searched}
              onChange={(value) => {
                const ufItem = ufs.find((uf) => uf.value === value);
                if (ufItem) {
                  form.setFieldsValue({
                    state: ufItem.name,
                  });
                  setSelectedUF(ufItem.value);
                } else {
                  form.setFieldsValue({
                    city: null,
                    cityCode: null,
                  });
                }
              }}
              filterOption={selectSearcher}
            >
              {ufs.map(({ value, name }) => (
                <Option key={value} value={value} label={name}>
                  {name}
                </Option>
              ))}
            </Select>
          </Item>
        </Col>
        <Col {...sizes.twoThirds} key="cityCode">
          {availableCityes[0] && (
            <Item
              name="cityCode"
              rules={[{ required: true, message: 'Por favor selecione a cidade.' }]}
              label={'Cidade'}
            >
              <Select
                showSearch
                disabled={loading || !searched}
                className="gyramais-input"
                optionFilterProp="children"
                filterOption={(input, option) =>
                  option.props.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                }
                onChange={(value) => {
                  const cityItem = availableCityes.find((city) => city.value === value);
                  form.setFieldsValue({
                    city: cityItem ? cityItem.name : null,
                  });
                }}
                placeholder="Cidade"
                style={{ width: '100%' }}
              >
                {availableCityes.map(({ value, name, uf }) => (
                  <Option key={value} value={value} label={name} uf={uf}>
                    {name}
                  </Option>
                ))}
              </Select>
            </Item>
          )}
        </Col>
        <Col {...sizes.full} key="district">
          <Item
            name="district"
            rules={[{ required: true, message: 'Por favor insira o bairro.' }]}
            label={'Bairro'}
          >
            <Input disabled={!searched} loading={loading} type="text" placeholder="Bairro" />
          </Item>
        </Col>
        <Col {...sizes.oneThird} key="prefix">
          <Item name="prefix" label={'Prefixo'}>
            <Select
              className="gyramais-input"
              loading={loading}
              placeholder="Prefixo"
              disabled={!searched}
            >
              {addressPrefix.map((pref) => (
                <Option key={pref} value={pref}>
                  {pref}
                </Option>
              ))}
            </Select>
          </Item>
        </Col>
        <Col {...sizes.twoThirds} key="street">
          <Item name="street" label={'Endereço'}>
            <Input loading={loading} type="text" disabled={!searched} placeholder="Endereço" />
          </Item>
        </Col>
        <Col {...sizes.oneThird} key="number">
          <Item
            name="number"
            rules={[
              {
                required: true,
                whitespace: true,
                message: 'Por favor insira um número válido.',
                max: 10,
                pattern: '^[0-9]*$',
              },
            ]}
            label={'Número'}
          >
            <Input disabled={!searched} loading={loading} type="text" placeholder="Número" />
          </Item>
        </Col>
        <Col {...sizes.twoThirds} key="complement">
          <Item
            name="complement"
            rules={[
              { required: false, max: 49, message: 'Complemento com no máximo 49 caracteres' },
            ]}
            label={'Complemento'}
          >
            <Input disabled={!searched} loading={loading} type="text" placeholder="Complemento" />
          </Item>
        </Col>
      </Row>
    </Form>
  );
};

export default AddressForm;
